import { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  DatePicker,
  message,
  Spin,
  Tooltip,
} from 'antd';
import {
  ShoppingCartOutlined,
  UserOutlined,
  InboxOutlined,
  EuroOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { formatPrice } from '../../utils/format';

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface DashboardStats {
  productCount: number;
  clientCount: number;
  orderCount: number;
  totalRevenue: number;
  recentProducts: any[];
  recentOrders: any[];
}

interface ChartDataItem {
  date: string;
  count: number;
  total: number;
  tooltipText: string;
}

const statusColors: Record<string, string> = {
  nieuw: 'blue',
  in_behandeling: 'orange',
  verzonden: 'purple',
  afgeleverd: 'green',
  geannuleerd: 'red',
};

const statusLabels: Record<string, string> = {
  nieuw: 'Nieuw',
  in_behandeling: 'In behandeling',
  verzonden: 'Verzonden',
  afgeleverd: 'Afgeleverd',
  geannuleerd: 'Geannuleerd',
};

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    productCount: 0,
    clientCount: 0,
    orderCount: 0,
    totalRevenue: 0,
    recentProducts: [],
    recentOrders: [],
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs(),
  ]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('Fetching dashboard data...');

      // Get count of all products directly from the database
      const { data: productData, error: productError } = await supabase
        .from('producten')
        .select('id')
        .limit(1000);
        
      const productCount = productData?.length || 0;
      console.log('Products found:', productCount);
      
      if (productError) {
        console.error('Error fetching products:', productError);
      }

      // Get all product access entries to count unique clients
      const { data: accessData, error: accessError } = await supabase
        .from('product_access')
        .select('user_id')
        .limit(1000);
        
      // Count unique user IDs
      const uniqueUserIds = new Set();
      accessData?.forEach(item => uniqueUserIds.add(item.user_id));
      const clientCount = uniqueUserIds.size;
      console.log('Unique clients found:', clientCount);
      
      if (accessError) {
        console.error('Error fetching client data:', accessError);
      }

      // Get all recent products
      const { data: recentProducts, error: recentProductsError } = await supabase
        .from('producten')
        .select('*')
        .order('bijgewerkt_op', { ascending: false })
        .limit(5);
        
      console.log('Recent products found:', recentProducts?.length || 0);
      
      if (recentProductsError) {
        console.error('Error fetching recent products:', recentProductsError);
      }

      // Get all orders
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('*');
        
      const orderCount = orderData?.length || 0;
      console.log('Orders found:', orderCount);
      
      // Calculate total revenue
      const totalRevenue = orderData?.reduce(
        (sum, order) => sum + (Number(order.totaal_bedrag) || 0), 
        0
      ) || 0;
      
      console.log('Total revenue calculated:', totalRevenue);
      
      if (orderError) {
        console.error('Error fetching orders:', orderError);
      }

      // Get recent orders without join
      const { data: recentOrders, error: recentOrdersError } = await supabase
        .from('orders')
        .select('*')
        .order('aangemaakt_op', { ascending: false })
        .limit(5);
        
      console.log('Recent orders found:', recentOrders?.length || 0);
      
      if (recentOrdersError) {
        console.error('Error fetching recent orders:', recentOrdersError);
      }

      // Update the stats
      setStats({
        productCount: productCount,
        clientCount: clientCount,
        orderCount: orderCount,
        totalRevenue: totalRevenue,
        recentProducts: recentProducts || [],
        recentOrders: recentOrders || []
      });

      console.log('Dashboard data updated successfully');
    } catch (error) {
      console.error('Error in fetchDashboardData:', error);
      message.error('Kon dashboard gegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  // Process data for the graph
  const orderChartData = useMemo(() => {
    if (!stats.recentOrders?.length) {
      return [];
    }

    const startDate = dateRange[0].startOf('day');
    const endDate = dateRange[1].endOf('day');
    
    // Create a map for all dates in range
    const dateMap: Record<string, ChartDataItem> = {};
    let current = startDate.clone();
    
    // Ensure we have entries for all dates in the range
    while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
      const dateKey = current.format('DD-MM');
      dateMap[dateKey] = {
        date: dateKey,
        count: 0,
        total: 0,
        tooltipText: '0 orders (€ 0,00)'
      };
      current = current.add(1, 'day');
    }

    // Fill in order data
    stats.recentOrders.forEach(order => {
      const dateKey = dayjs(order.aangemaakt_op).format('DD-MM');
      if (dateMap[dateKey]) {
        dateMap[dateKey].count += 1;
        dateMap[dateKey].total += Number(order.totaal_bedrag) || 0;
        dateMap[dateKey].tooltipText = `${dateMap[dateKey].count} orders (${formatPrice(dateMap[dateKey].total)})`;
      }
    });

    // Convert to array and sort by date
    return Object.values(dateMap).sort((a: ChartDataItem, b: ChartDataItem) => {
      const [dayA, monthA] = a.date.split('-').map(Number);
      const [dayB, monthB] = b.date.split('-').map(Number);
      
      if (monthA !== monthB) return monthA - monthB;
      return dayA - dayB;
    });
  }, [stats.recentOrders, dateRange]);

  const productColumns = [
    {
      title: 'Artikelnummer',
      dataIndex: 'artikelnummer',
      key: 'artikelnummer',
    },
    {
      title: 'Naam',
      dataIndex: 'naam',
      key: 'naam',
    },
    {
      title: 'Prijs',
      dataIndex: 'prijs',
      key: 'prijs',
      render: (prijs: number) => formatPrice(prijs),
    },
    {
      title: 'Laatst Bijgewerkt',
      dataIndex: 'bijgewerkt_op',
      key: 'bijgewerkt_op',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Acties',
      key: 'actions',
      render: (_: unknown, record: any) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />}
          onClick={() => navigate(`/admin/products/${record.id}`)}
        >
          Bekijken
        </Button>
      ),
    },
  ];

  const orderColumns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => id.substring(0, 8),
    },
    {
      title: 'Klant',
      dataIndex: 'klant_id',
      key: 'client',
      render: (klantId: string) => klantId ? klantId.substring(0, 8) : 'Onbekend',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: 'Bedrag',
      dataIndex: 'totaal_bedrag',
      key: 'totaal_bedrag',
      render: (amount: number) => formatPrice(Number(amount)),
    },
    {
      title: 'Datum',
      dataIndex: 'aangemaakt_op',
      key: 'aangemaakt_op',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Gewenste leverdatum',
      dataIndex: 'gewenste_leverdatum',
      key: 'gewenste_leverdatum',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Gewenste leverdatum',
      dataIndex: 'verwachte_leverdatum',
      key: 'verwachte_leverdatum',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Acties',
      key: 'actions',
      render: (_: unknown, record: any) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />}
          onClick={() => navigate(`/admin/orders/${record.id}`)}
        >
          Bekijken
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Dashboard</Title>
        <div style={{ 
          marginBottom: '16px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: '16px'
        }}>
          <Title level={5} style={{ margin: 0 }}>Overzicht</Title>
          <RangePicker 
            value={dateRange} 
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([dates[0], dates[1]]);
              }
            }}
            allowClear={false}
            format="DD-MM-YYYY"
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Producten"
                  value={stats.productCount}
                  prefix={<InboxOutlined />}
                />
                <div style={{ marginTop: '12px' }}>
                  <Button 
                    type="link" 
                    style={{ padding: 0 }}
                    onClick={() => navigate('/admin/products')}
                  >
                    Bekijk alle producten
                  </Button>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Klanten"
                  value={stats.clientCount}
                  prefix={<UserOutlined />}
                />
                <div style={{ marginTop: '12px' }}>
                  <Button 
                    type="link" 
                    style={{ padding: 0 }}
                    onClick={() => navigate('/admin/companies')}
                  >
                    Bekijk alle klanten
                  </Button>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Bestellingen"
                  value={stats.orderCount}
                  prefix={<ShoppingCartOutlined />}
                />
                <div style={{ marginTop: '12px' }}>
                  <Button 
                    type="link" 
                    style={{ padding: 0 }}
                    onClick={() => navigate('/admin/orders')}
                  >
                    Bekijk alle bestellingen
                  </Button>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Totale omzet"
                  value={stats.totalRevenue}
                  precision={2}
                  prefix={<EuroOutlined />}
                  suffix="€"
                />
              </Card>
            </Col>
          </Row>

          <Card style={{ marginTop: '24px' }}>
            <Title level={4}>Bestellingen per dag</Title>
            <div style={{ width: '100%', height: 300 }}>
              {orderChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={orderChartData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip formatter={(value, name, props) => [props.payload.tooltipText]} />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#8884d8" 
                      activeDot={{ r: 8 }} 
                      name="Bestellingen"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <p>Geen bestellingen gevonden in de geselecteerde periode</p>
                </div>
              )}
            </div>
          </Card>

          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col span={24} lg={12}>
              <Card>
                <Title level={4}>Laatste Producten</Title>
                <Table
                  columns={[
                    {
                      title: 'Artikelnummer',
                      dataIndex: 'artikelnummer',
                      key: 'artikelnummer',
                    },
                    {
                      title: 'Naam',
                      dataIndex: 'naam',
                      key: 'naam',
                    },
                    {
                      title: 'Bijgewerkt',
                      dataIndex: 'bijgewerkt_op',
                      key: 'bijgewerkt_op',
                      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
                    },
                    {
                      title: 'Acties',
                      key: 'actions',
                      render: (_, record) => (
                        <Button 
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() => navigate(`/admin/products/${record.id}`)}
                        />
                      ),
                    },
                  ]}
                  dataSource={stats.recentProducts}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <Button 
                    type="primary" 
                    onClick={() => navigate('/admin/products')}
                  >
                    Alle producten
                  </Button>
                </div>
              </Card>
            </Col>
            <Col span={24} lg={12}>
              <Card>
                <Title level={4}>Laatste Bestellingen</Title>
                <Table
                  columns={[
                    {
                      title: 'Ordernummer',
                      dataIndex: 'ordernummer',
                      key: 'ordernummer',
                      render: (text: string, record: any) => text || `#${record.id.substr(0, 8)}`,
                    },
                    {
                      title: 'Klant',
                      dataIndex: 'klant_id',
                      key: 'klant_id',
                      render: (klantId: string) => (
                        <Button 
                          type="link" 
                          onClick={() => navigate(`/admin/companies/${klantId}`)}
                          style={{ padding: 0 }}
                        >
                          {klantId.substr(0, 8)}...
                        </Button>
                      ),
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      render: (status: string) => (
                        <Tag color={statusColors[status] || 'default'}>
                          {statusLabels[status] || status}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Bedrag',
                      dataIndex: 'totaal_bedrag',
                      key: 'totaal_bedrag',
                      render: (amount: number) => formatPrice(Number(amount)),
                    },
                    {
                      title: 'Datum',
                      dataIndex: 'aangemaakt_op',
                      key: 'aangemaakt_op',
                      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
                    },
                    {
                      title: 'Acties',
                      key: 'actions',
                      render: (_, record) => (
                        <Button 
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() => navigate(`/admin/orders/${record.id}`)}
                        />
                      ),
                    },
                  ]}
                  dataSource={stats.recentOrders}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <Button 
                    type="primary" 
                    onClick={() => navigate('/admin/orders')}
                  >
                    Alle bestellingen
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
} 